/** Тонкий клиент Gramps Web API: JWT, автообновление токена, типизированный GET. */

const ACCESS = 'ft.access'
const REFRESH = 'ft.refresh'
const USER = 'ft.user'

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
  }
}

let accessToken: string | null = localStorage.getItem(ACCESS)
let refreshToken: string | null = localStorage.getItem(REFRESH)

export const isLoggedIn = () => !!refreshToken

/** Логин вошедшего. Нужен, чтобы «я в древе» помнилось для каждого
 *  члена семьи отдельно: аккаунтов на одном компьютере может быть несколько. */
export const currentUser = () => localStorage.getItem(USER) || ''

/* Токен для адресов картинок берут через хук useApiToken: обычная функция
   не сообщала React о смене токена, и <img> оставались со старым адресом.
   Отсюда и «повисел, пощёлкал разделы — аватарки пропали». */

/* Кто ждёт смены токена.
 *
 * Картинки просят с токеном в АДРЕСЕ (<img> не умеет слать заголовки),
 * а значит мимо request() и мимо его обновления на 401. Когда токен
 * протухал, аватарки просто гасли до первого запроса, который его
 * обновит: типичное «повисел, пощёлкал разделы — фотографии пропали».
 * Теперь смена токена рассылается подписчикам, и картинки
 * перерисовываются с новым адресом сами. */
const listeners = new Set<() => void>()

export const subscribeToken = (cb: () => void) => {
  listeners.add(cb)
  return () => { listeners.delete(cb) }
}

export const getToken = () => accessToken || ''

function store(access: string | null, refresh?: string | null) {
  const changed = access !== accessToken
  accessToken = access
  if (access) localStorage.setItem(ACCESS, access)
  else localStorage.removeItem(ACCESS)
  if (refresh !== undefined) {
    refreshToken = refresh
    if (refresh) localStorage.setItem(REFRESH, refresh)
    else localStorage.removeItem(REFRESH)
  }
  if (changed) {
    scheduleRefresh()
    listeners.forEach(cb => cb())
  }
}

/** Когда истекает access-токен, мс эпохи. 0 — не разобрали. */
function expiresAt(t: string | null): number {
  if (!t) return 0
  try {
    const p = t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const exp = JSON.parse(atob(p)).exp
    return typeof exp === 'number' ? exp * 1000 : 0
  } catch {
    return 0
  }
}

/* Обновляемся ЗАРАНЕЕ, за минуту до конца, а не по факту отказа: к моменту
   отказа картинки уже погасли, и чинить их поздно. */
let timer: ReturnType<typeof setTimeout> | null = null

function scheduleRefresh() {
  if (timer) clearTimeout(timer)
  timer = null
  const exp = expiresAt(accessToken)
  if (!exp || !refreshToken) return
  timer = setTimeout(() => { void refresh() }, Math.max(5000, exp - Date.now() - 60_000))
}

/** Токен вот-вот кончится или уже кончился. */
const stale = () => {
  const exp = expiresAt(accessToken)
  return !exp || exp - Date.now() < 90_000
}

/** Обновить, если пора. Зовётся из вкладки, вернувшейся из фона, и
 *  из картинки, не сумевшей загрузиться. */
export function refreshIfStale(): void {
  if (refreshToken && stale()) void refresh()
}

if (typeof document !== 'undefined') {
  /* В фоновой вкладке браузер душит таймеры, и наш плановый обход мог не
     сработать вовсе — а это ровно тот случай, с которого всё началось:
     вкладку оставили висеть, вернулись, начали щёлкать. */
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshIfStale()
  })
  window.addEventListener('online', refreshIfStale)
}

// токен приехал из localStorage мимо store() — обход надо завести вручную
scheduleRefresh()

export async function login(username: string, password: string) {
  const r = await fetch('/api/token/', {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({username, password}),
  })
  if (!r.ok) {
    // Gramps Web отдаёт именно 403 на неверные учётные данные, не 401
    const wrong = r.status === 403 || r.status === 401
    throw new ApiError(r.status, wrong ? 'Неверный логин или пароль' : `Ошибка входа (${r.status})`)
  }
  const d = await r.json()
  localStorage.setItem(USER, username)
  store(d.access_token, d.refresh_token)
}

export function logout() {
  store(null, null)
}

/* Обновление access-токена.
 *
 * Две тонкости, на которых мы уже обожглись:
 *
 * 1. На /api/token/ и /api/token/refresh/ стоит лимит 1 запрос в секунду.
 *    Загрузка древа шлёт три запроса разом; когда access протухает (живёт
 *    15 минут), все три получают 401 и синхронно ломятся обновляться —
 *    первый проходит, остальные ловят 429. Поэтому обновление
 *    однопоточное: параллельные вызовы ждут один и тот же запрос.
 *
 * 2. Стирать токены можно ТОЛЬКО если сервер сказал, что refresh негоден
 *    (401/422). На 429, 5xx и обрыв связи — молчим и пробуем позже:
 *    раньше любая заминка выкидывала из сессии, хотя refresh-токен
 *    на сервере бессрочный (JWT_REFRESH_TOKEN_EXPIRES = False). */
let refreshing: Promise<boolean> | null = null

const sleep = (ms: number) => new Promise(res => setTimeout(res, ms))

function refresh(): Promise<boolean> {
  if (!refreshToken) return Promise.resolve(false)
  if (refreshing) return refreshing

  refreshing = (async () => {
    try {
      for (let attempt = 0; attempt < 2; attempt++) {
        const r = await fetch('/api/token/refresh/', {
          method: 'POST',
          headers: {Authorization: `Bearer ${refreshToken}`},
        })
        if (r.ok) {
          const d = await r.json()
          store(d.access_token)
          return true
        }
        if (r.status === 401 || r.status === 422) {
          store(null, null)   // refresh действительно негоден — нужен вход
          return false
        }
        // 429: лимит ровно 1 запрос в секунду — ждём и пробуем ещё раз
        if (r.status === 429 && attempt === 0) {
          await sleep(1200)
          continue
        }
        return false
      }
      return false
    } catch {
      return false          // сеть моргнула — сессию не трогаем
    } finally {
      refreshing = null
    }
  })()

  return refreshing
}

/**
 * Токен, с которым ушёл запрос, мог протухнуть — но пока он летел, соседний
 * запрос уже мог всё обновить. Тогда повторяем сразу, без второго обращения
 * к /token/refresh/.
 *
 * Именно этого не хватало: три запроса древа получают 401 ВРАЗНОБОЙ, первый
 * успевает обновиться и снять флаг однопоточности, третий идёт обновляться
 * заново и упирается в лимит 1/сек. Запрос не повторялся и показывал
 * пользователю сырое «Token has expired».
 */
async function ensureFresh(tokenUsed: string | null): Promise<boolean> {
  if (accessToken && accessToken !== tokenUsed) return true
  return refresh()
}

/** Запрос с одной автоматической попыткой обновить токен на 401. */
async function request<T>(method: string, path: string, params?: Record<string, string | number>, body?: unknown): Promise<T> {
  const qs = params
    ? '?' + Object.entries(params).map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&')
    : ''
  const url = `/api${path}${qs}`

  const call = () =>
    fetch(url, {
      method,
      headers: {
        ...(accessToken ? {Authorization: `Bearer ${accessToken}`} : {}),
        ...(body !== undefined ? {'Content-Type': 'application/json'} : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })

  const used = accessToken
  let r = await call()
  if (r.status === 401 && (await ensureFresh(used))) r = await call()

  if (!r.ok) {
    // Gramps кладёт причину в {"error": {"message": …}} — без неё
    // «сохранить не удалось» бесполезно для отладки
    let detail = ''
    try {
      const j = await r.json()
      detail = j?.error?.message || j?.message || ''
    } catch {
      /* тело не json — обойдёмся кодом */
    }
    throw new ApiError(r.status, humanError(r.status, detail, path))
  }
  return r.json() as Promise<T>
}

/** Серверные формулировки родне ничего не говорят — переводим на человеческий. */
function humanError(status: number, detail: string, path: string): string {
  if (detail === "Object has changed") {
    return "Пока вы правили, эти данные изменил кто-то ещё. Обновите страницу и повторите."
  }
  if (status === 401 || /token/i.test(detail)) {
    return "Сессия истекла — войдите заново"
  }
  if (status === 429) return "Слишком часто — подождите пару секунд и повторите"
  if (status === 403) return "Не хватает прав на это действие"
  if (status === 404) return "Запись не найдена — возможно, её уже удалили"
  if (status >= 500) return "Сервер не справился с запросом. Попробуйте ещё раз"
  return detail || `Запрос ${path} вернул ${status}`
}

export const apiGet = <T>(path: string, params?: Record<string, string | number>) =>
  request<T>('GET', path, params)

export const apiPost = <T>(path: string, body: unknown, params?: Record<string, string | number>) =>
  request<T>('POST', path, params, body)

/** ВНИМАНИЕ: PUT собирает объект заново из присланного тела — непереданные
 *  поля обнулятся. Всегда читать объект целиком, менять и слать целиком. */
export const apiPut = <T>(path: string, body: unknown) => request<T>('PUT', path, undefined, body)

export const apiDelete = <T>(path: string) => request<T>('DELETE', path)

/** Загрузка файла: Gramps ждёт СЫРЫЕ байты в теле, а тип файла берёт из
 *  Content-Type. Никакого multipart — поэтому отдельно от request(). */
export async function apiUpload<T>(path: string, blob: Blob): Promise<T> {
  const call = () =>
    fetch(`/api${path}`, {
      method: 'POST',
      headers: {
        ...(accessToken ? {Authorization: `Bearer ${accessToken}`} : {}),
        'Content-Type': blob.type || 'application/octet-stream',
      },
      body: blob,
    })

  const used = accessToken
  let r = await call()
  if (r.status === 401 && (await ensureFresh(used))) r = await call()

  if (!r.ok) {
    let detail = ''
    try {
      const j = await r.json()
      detail = j?.error?.message || j?.message || ''
    } catch {
      /* тело не json */
    }
    throw new ApiError(r.status, humanError(r.status, detail, '/media/'))
  }
  return r.json() as Promise<T>
}
